import { getMapTelemetryConfig } from './config';
import { EClustersFailReason, EPinsUpdateTrigger, TELEMETRY_KEYS } from './constants';
import { NOOP_MAP_TELEMETRY } from './noopMapTelemetry';
import { wasPageHiddenSince } from './pageVisibility';
import {
  clearPendingPinsUpdate,
  getPendingPinsUpdate,
  IPendingPinsUpdate,
  markPendingPinsUpdateDataReady,
  startPendingPinsUpdate,
} from './pinsUpdateState';
import { getPinsCountBucket, resolveFailReason } from './utils';

import type { IMapTelemetry, IMapTelemetryDependencies } from './types';

/**
 * Сквозное время загрузки карты отправляется один раз за страницу.
 * Флаг держим на уровне модуля, а не экземпляра фасада: источником сигнала служит слой пинов,
 * и при его пересоздании в метрику попал бы второй, заведомо неверный замер.
 */
let isMapLoadReported = false;

/** Начало навигации в шкале performance.now(): точка отсчёта сквозных метрик страницы */
const PAGE_START = 0;

/** Только для тестов: сбрасывает признак уже отправленного замера загрузки */
export function resetMapLoadReported() {
  isMapLoadReported = false;
}

/**
 * Единая точка отправки технических метрик карты.
 *
 * Собран фасадом, а не разрозненными вызовами increment, по трём причинам:
 * ключи лежат в одном реестре и не расползаются, разрезы дописываются одинаково во всех метриках,
 * и вызов безопасен, когда телеметрии нет, — например в тестах.
 */
export function createMapTelemetry({
  telemetry,
  deviceType,
  logger,
  config,
}: IMapTelemetryDependencies): IMapTelemetry {
  const { enabled, sessionSamplingRate, renderSamplingRate } = getMapTelemetryConfig(config);

  /**
   * Рубильник и доля сессий из Runtime Settings. Возвращаем пустую реализацию, а не проверяем
   * флаги в каждом методе: так выключение гарантированно доходит до всех точек вызова.
   *
   * Решение о сессии принимается здесь один раз за страницу. Несэмплированная сессия не шлёт
   * ни одной метрики и, что важнее для приёмника, ни одного HTTP-запроса.
   */
  if (!enabled || Math.random() >= sessionSamplingRate) {
    return NOOP_MAP_TELEMETRY;
  }

  /**
   * Два набора разрезов вместо одного — так дерево метрик остаётся подъёмным.
   * DEVICE нужен там, где по метрике считается SLO: пороги у мобильных и десктопа разные (Q15).
   * NONE — у вспомогательных таймеров: их смотрят при разборе инцидента как одну линию.
   *
   * Разреза по маршруту нет. Решение Q16 предполагало, что у /map-novostroyki/ сопоставимый
   * трафик, но по логам за неделю её открывают около 19 раз в час против 4,7 раза в секунду
   * у /map/ — одна тысячная. Перцентили по такой выборке ничего не значат, а число серий с разрезом
   * по устройству он бы удвоил, и половина из них пустовала бы пять лет.
   */
  const SCOPE = {
    device: String(deviceType),
    none: '',
  };

  const withScope = (key: string, scope: string) => (scope ? `${key}.${scope}` : key);

  const increment = (key: string, value?: number) => {
    const fullKey = withScope(key, SCOPE.device);

    /** Не передаём undefined в пакет телеметрии: у increment своё значение по умолчанию */
    if (value === undefined) {
      telemetry?.increment(fullKey);

      return;
    }

    telemetry?.increment(fullKey, value);
  };

  /** statsd работает с целыми миллисекундами, дробная часть performance.now() ему не нужна */
  const timing = (key: string, durationMs: number, scope: string = SCOPE.device) => {
    telemetry?.timing(withScope(key, scope), Math.round(durationMs));
  };

  const closePinsUpdate = (pendingUpdate: IPendingPinsUpdate) => {
    /**
     * Замер, захвативший уход вкладки в фон, отражает не скорость карты, а паузу до возвращения
     * пользователя: rAF и таймеры в скрытой вкладке заморожены. Такой сэмпл выбрасываем.
     */
    if (!wasPageHiddenSince(pendingUpdate.startedAt)) {
      timing(`${TELEMETRY_KEYS.pinsUpdate}.${pendingUpdate.trigger}`, performance.now() - pendingUpdate.startedAt);
    }

    clearPendingPinsUpdate();
  };

  return {
    trackMapInitialized: () => {
      /** Во вкладке, открытой в фоне, ymaps инициализируется лениво — замер был бы про другое */
      if (!wasPageHiddenSince(PAGE_START)) {
        timing(TELEMETRY_KEYS.mapInit, performance.now(), SCOPE.none);
      }
    },

    trackPinsRendered: () => {
      if (!isMapLoadReported) {
        /**
         * Флаг ставим и когда сэмпл выброшен: следующая отрисовка дала бы ещё более далёкое
         * от правды число, ведь отсчёт всё равно идёт от начала навигации.
         */
        isMapLoadReported = true;

        if (!wasPageHiddenSince(PAGE_START)) {
          /** performance.now() отсчитывается от начала навигации, это сразу искомая длительность */
          timing(TELEMETRY_KEYS.mapLoad, performance.now());
        }
      }

      const pendingUpdate = getPendingPinsUpdate();

      if (!pendingUpdate?.isDataReady) {
        return;
      }

      closePinsUpdate(pendingUpdate);
    },

    startPinsUpdate: (trigger: EPinsUpdateTrigger, startedAt: number = performance.now()) =>
      startPendingPinsUpdate(trigger, startedAt),

    trackPinsUpdateRequestStarted: () => {
      const pendingUpdate = getPendingPinsUpdate();

      if (!pendingUpdate) {
        return;
      }

      if (wasPageHiddenSince(pendingUpdate.startedAt)) {
        return;
      }

      timing(
        `${TELEMETRY_KEYS.pinsUpdateDelay}.${pendingUpdate.trigger}`,
        performance.now() - pendingUpdate.startedAt,
        SCOPE.none,
      );
    },

    /**
     * Загружать нечего, значит на экране уже корректные пины и взаимодействие обслужено.
     * Без этого в SLI 2 попадали бы только промахи мимо загруженных тайлов, и перцентиль
     * нельзя было бы отличить от изменения доли таких промахов.
     */
    trackPinsUpdateSkipped: () => {
      const pendingUpdate = getPendingPinsUpdate();

      if (pendingUpdate) {
        closePinsUpdate(pendingUpdate);
      }
    },

    trackClustersRequestDuration: (durationMs: number) =>
      timing(TELEMETRY_KEYS.clustersRequest, durationMs, SCOPE.none),

    trackClustersSucceed: () => {
      markPendingPinsUpdateDataReady();
      increment(TELEMETRY_KEYS.clustersSucceed);
    },

    trackClustersFailed: (error: unknown) => {
      const reason: EClustersFailReason = resolveFailReason(error);

      /**
       * Шлём и агрегат, и сегментированную метрику: по агрегату считается SLO,
       * по сегментам разбираются инциденты.
       */
      increment(TELEMETRY_KEYS.clustersFail);
      increment(`${TELEMETRY_KEYS.clustersFailReason}.${reason}`);

      /** Пины не обновятся — замер SLI 2 закрывать нечем, иначе он достанется следующей перерисовке */
      clearPendingPinsUpdate();
    },

    trackClustersCancelled: () => increment(TELEMETRY_KEYS.clustersCancelled),

    trackClustersRetried: () => increment(TELEMETRY_KEYS.clustersRetry),

    trackTileRendered: (pinsCount: number, durationMs: number) => {
      /** Пустой тайл рисуется мгновенно и только занизил бы перцентили */
      if (pinsCount === 0 || Math.random() >= renderSamplingRate) {
        return;
      }

      timing(`${TELEMETRY_KEYS.pinsTileRender}.${getPinsCountBucket(pinsCount)}`, durationMs);
    },

    trackPinRenderErrors: (errorsCount: number, error: unknown) => {
      if (errorsCount === 0) {
        return;
      }

      /**
       * Один вызов на тайл, но со счётчиком всех упавших в нём пинов:
       * инкремент на каждый пин залил бы приёмник, а терять количество не хочется.
       */
      increment(TELEMETRY_KEYS.pinsRenderError, errorsCount);
      /** В catch может прилететь что угодно, а логгер принимает только строку или Error */
      logger?.error(error instanceof Error ? error : String(error), {
        domain: 'shared/map-search/utils/ymapsModules/PointsLayer#drawTile()',
      });
    },

    trackPinResultsAttempt: () => increment(TELEMETRY_KEYS.pinResultsAttempt),

    trackPinPopupBlocked: () => increment(TELEMETRY_KEYS.pinResultsPopupBlocked),

    trackChatAttempt: () => increment(TELEMETRY_KEYS.chatAttempt),

    trackChatNoSource: () => increment(TELEMETRY_KEYS.chatNoSource),

    trackPinsData: (deliveredCount: number, preparedCount: number) => {
      if (deliveredCount === 0) {
        return;
      }

      /**
       * Два счётчика вместо одной разницы: по ним в Grafana видно и объём выдачи,
       * и долю потерь. Одна разница без знаменателя ничего не говорит.
       */
      increment(TELEMETRY_KEYS.pinsDelivered, deliveredCount);
      increment(TELEMETRY_KEYS.pinsPrepared, preparedCount);

      const invalidCount = deliveredCount - preparedCount;

      /** В норме счётчик пустой, поэтому любое ненулевое значение — сигнал расхождения с контрактом */
      if (invalidCount > 0) {
        increment(TELEMETRY_KEYS.pinsDataInvalid, invalidCount);
      }
    },
  };
}
